import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft, faCashRegister } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../components/Button';
import { NumericKeypad } from '../../../../components/Keypad/NumericKeypad';
import classes from './open-session-modal.module.scss';

const MAX_DIGITS = 7;
const MAX_OPENING_CASH = 999999; // mirror the backend DTO bound (openingCash.max)
const round1 = (value: number) => Math.round(value * 10) / 10;

export type OpenSessionModalProps = {
  isOpen: boolean;
  mode: 'open' | 'edit';
  isManager: boolean;
  isSaving?: boolean;
  existingAmount?: number;
  onOpenDrawer: () => void;
  onConfirm: (openingCash: number) => void;
  onClose: () => void;
};

// Summary: Odoo opening-control parity modal — declares/edits the opening cash float (RTL).
const OpenSessionModalComponent: React.FC<OpenSessionModalProps> = ({
  isOpen,
  mode,
  isManager,
  isSaving = false,
  existingAmount = 0,
  onOpenDrawer,
  onConfirm,
  onClose,
}) => {
  const [buffer, setBuffer] = useState<string>('');
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setBuffer(mode === 'edit' && existingAmount > 0 ? String(round1(existingAmount)) : '');
      hasInteractedRef.current = false;
    }
  }, [isOpen, mode, existingAmount]);

  if (!isOpen) return null;

  // Opening cash is optional; empty/'.' means 0 (a shift can open with no float).
  const rawValue = buffer === '' || buffer === '.' ? 0 : Number(buffer);
  const numericValue = Number.isFinite(rawValue) ? round1(rawValue) : NaN;
  const isValid = Number.isFinite(numericValue) && numericValue >= 0 && numericValue <= MAX_OPENING_CASH;

  const pressDigit = (digit: string) => {
    setBuffer((prev) => {
      const base = hasInteractedRef.current ? prev : '';
      hasInteractedRef.current = true;
      const candidate = base + digit;
      if (candidate.replace('.', '').length > MAX_DIGITS) return prev;
      return candidate.replace(/^0(\d)/, '$1');
    });
  };

  const pressDot = () => {
    setBuffer((prev) => {
      hasInteractedRef.current = true;
      if (prev.includes('.')) return prev;
      return prev === '' ? '0.' : prev + '.';
    });
  };

  const pressBackspace = () => {
    hasInteractedRef.current = true;
    setBuffer((prev) => prev.slice(0, -1));
  };

  const handleConfirm = () => {
    if (!isValid || isSaving) return;
    onConfirm(numericValue);
  };

  const displayValue = buffer === '' ? '0' : buffer;
  const title = mode === 'edit' ? 'עדכון מזומן פתיחה' : 'פתיחת משמרת';
  const confirmLabel = mode === 'edit' ? 'עדכן מזומן פתיחה' : 'התחל משמרת';

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={title} data-testid="session-open-modal">
      <div className={classes.modal}>
        <div className={classes.header}>
          <h2 className={classes.title}>{title}</h2>
        </div>

        <div className={classes.body}>
          <div className={classes.amountRow}>
            <div className={classes.amountCell}>
              <span className={classes.amountLabel}>מזומן פתיחה</span>
              <span className={classes.amountValue}>₪ {displayValue}</span>
            </div>
            {isManager ? (
              <Button
                variant="save"
                size="sm"
                className={classes.drawerButton}
                onClick={onOpenDrawer}
                disabled={isSaving}
                dataTestId="session-open-drawer"
              >
                <FontAwesomeIcon icon={faCashRegister as IconProp} />
                <span>פתח מגירה</span>
              </Button>
            ) : (
              <span className={classes.drawerHint}>רק מנהל יכול לפתוח מגירה</span>
            )}
          </div>

          <NumericKeypad
            dataTestId="session-open"
            keypadClassName={classes.keypad}
            keyClassName={classes.key}
            disabled={isSaving}
            onDigit={pressDigit}
            onBackspace={pressBackspace}
            backspaceDisabled={buffer === ''}
            backspaceAriaLabel="מחק ספרה"
            backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
            leftKey={{ label: '.', className: classes.key, onClick: pressDot, ariaLabel: 'נקודה עשרונית' }}
          />

          <div className={classes.actions}>
            <Button variant="cancel" fullWidth onClick={onClose} disabled={isSaving} dataTestId="session-open-cancel">
              ביטול
            </Button>
            <Button
              variant={mode === 'edit' ? 'save' : 'confirm'}
              fullWidth
              onClick={handleConfirm}
              disabled={!isValid || isSaving}
              loading={isSaving}
              loadingLabel="שומר..."
              dataTestId="session-open-confirm"
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const OpenSessionModal = React.memo(OpenSessionModalComponent);
